/**
 * "A newer Karr Copilot is out" (copilotupd, 30 Aug 2026).
 *
 * Founder: when an update comes it should be in the notifications and show
 * red until upgraded. So, once per session on any Karr page where the Copilot
 * is installed: ask the extension its version, ask the bucket its newest, and
 * when the bucket is ahead (a) hold that in a store the Settings tab reads for
 * its red mark, (b) raise ONE bell notification per version through
 * `copilot_note_update`, and (c) hand `latest` to the extension with every
 * filing so the pill on MCA shows a red dot too.
 *
 * copupgrade (25 Sep 2026). Founder: the moment the version differs, a
 * licensed person gets a popup asking them to update, on EVERY reload until
 * they do. So the bucket is asked on every page load (no session cache: a zip
 * uploaded mid-day reaches the next reload), `CopilotUpdatePopup` draws the
 * ask, and its Update now hands `wantUpdate` to the Settings card, which runs
 * its own Download and guided update. Unlicensed people never see it: the
 * bucket answers only a live licence.
 *
 * Pure decisions, one hook, one store. The decisions are tested; the hook only
 * plumbs.
 */
import { useEffect } from 'react';
import { create } from 'zustand';
import { supabase } from '@/db/supabase';
import { useAuth } from '@/shared/hooks/useAuth';
import { myPracticeMembership } from '@/shared/config/officeIdentity';
import { copilotExtensionId, identifyExtension } from './handoff';
import { compareVersions } from './versionCompare';

export interface CopilotUpdate {
    installed: string;
    latest: string;
}

interface UpdateStore {
    /** Set only while the installed build is older than the bucket's newest. */
    update: CopilotUpdate | null;
    /** The bucket's newest, whatever the installed version; null until asked. */
    latest: string | null;
    /** Stamped by the popup's "Update by hand"; the Settings card downloads and clears it. */
    wantUpdate: number | null;
    /** Stamped by the Settings card's Update button: open the popup even after Later. */
    forceOpen: number | null;
    /** Stamped when the one-press update finished, so the Settings card re-reads. */
    updatedAt: number | null;
    /** copfreshdir: Karr filled a new folder; the Settings card opens the guide for it. */
    freshStart: { folder: string; version: string; n: number } | null;
    set: (s: Partial<UpdateStore>) => void;
}

export const useCopilotUpdateStore = create<UpdateStore>((set) => ({
    update: null,
    latest: null,
    wantUpdate: null,
    forceOpen: null,
    updatedAt: null,
    freshStart: null,
    set: (s) => set(s),
}));

/** Older than the newest → the update to show; otherwise null. Pure. */
export function decideUpdate(installed: string | null, latest: string | null): CopilotUpdate | null {
    if (!installed || !latest) return null;
    return compareVersions(latest, installed) > 0 ? { installed, latest } : null;
}

/**
 * Whether this page load shows the update popup. Pure.
 *
 * Not while the guided install is already walking an update (its memo is set):
 * the guide reloads this page itself to look for the new Copilot, and a popup
 * on top of it would ask for the very thing the person is doing.
 */
export function showUpdatePopup(a: { update: CopilotUpdate | null; guideOpen: boolean; dismissed: boolean }): boolean {
    return !!a.update && !a.guideOpen && !a.dismissed;
}

/** A popup press older than this is stale: the card must not download on a later visit. */
export const UPDATE_REQUEST_MS = 2 * 60_000;

/**
 * The Settings card, holding the popup's Update now. Pure.
 *
 * - `wait`: nothing asked, or the card has not read the licence and the newest build yet.
 * - `download`: press Download for them.
 * - `drop`: asked too long ago, or the card offers no download (it says why itself).
 */
export function takeUpdateRequest(a: { wantUpdate: number | null; loaded: boolean; canDownload: boolean; busy: boolean; now: number }): 'wait' | 'download' | 'drop' {
    if (a.wantUpdate === null) return 'wait';
    if (a.now - a.wantUpdate > UPDATE_REQUEST_MS) return 'drop';
    if (!a.loaded || a.busy) return 'wait';
    return a.canDownload ? 'download' : 'drop';
}

/** The bell was rung for this version in this session; the server dedupes too. */
const NOTED_KEY = 'karr.copilot.updateNoted';

/**
 * Mounted once in AppLayout. Runs for practice members on a browser that has
 * the Copilot, on every page load, so the popup comes back on every reload.
 */
export function useCopilotUpdateCheck(): void {
    const orgs = useAuth((s) => s.orgs);
    const inPractice = myPracticeMembership(orgs) !== null;
    const set = useCopilotUpdateStore((s) => s.set);

    useEffect(() => {
        if (!inPractice || !copilotExtensionId()) return;
        let cancelled = false;
        (async () => {
            const who = await identifyExtension();
            if (cancelled || who.status !== 'identified') return;
            const installed = who.version || null;

            // Every load: a zip uploaded since the last one must reach the next reload.
            const chk = await supabase.functions.invoke('copilot-download', { body: { check: true } });
            const out = chk.data as { ok?: boolean; latest?: string | null } | null;
            const latest = !chk.error && out?.ok ? (out.latest ?? null) : null;
            if (cancelled) return;
            const update = decideUpdate(installed, latest);
            set({ update, latest });
            if (!update) return;
            let noted: string | null = null;
            try { noted = sessionStorage.getItem(NOTED_KEY); } catch { noted = null; }
            if (noted === update.latest) return;
            // The bell: one per person per version, deduped server-side.
            await supabase.rpc('copilot_note_update', { p_installed: update.installed, p_latest: update.latest });
            try { sessionStorage.setItem(NOTED_KEY, update.latest); } catch { /* private mode */ }
        })().catch(() => undefined);
        return () => { cancelled = true; };
    }, [inPractice, set]);
}
